import { useMemo } from 'react';
import { createTheme, useTheme } from '@mui/material/styles';
import { RateLimitError } from '../../../lib/api/client';

/**
 * ClinVea brand palette (logo + sign-in screens, see auth/authShared.tsx), shared with the public site
 * (backend resources/public-site/assets/site.css) so the hand-off from a doctor page doesn't change
 * colour under the patient. `action` is the brand blue #2f6bff deepened so white text meets WCAG AA.
 */
export const CARE = {
  action: '#2158d6',
  actionDeep: '#1a45ab',
  ink: '#0f2942',
  muted: '#56677f',
  ground: '#f4f7fc',
  tint: '#eaf1ff',
  verified: '#0b7d57',
  line: '#dde5f0',
  navy: '#0b1c30',
  gradient: 'linear-gradient(90deg, #2f6bff 0%, #12b5cf 100%)',
};

/** The ClinVea wordmark (a 160 px-high web copy of the light logo, same file as the public site's logo.png) and the product tag beside it. */
export const BRAND = { logo: '/assets/images/clinvea-care-logo.png?v=20261007', name: 'ClinVea', tag: 'Care' };

export const DATE = /^\d{4}-\d{2}-\d{2}$/;
export const TIME = /^\d{2}:\d{2}$/;
export const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type DayPart = 'morning' | 'afternoon' | 'evening';
export const partOf = (hhmm: string): DayPart => (hhmm < '12:00' ? 'morning' : hhmm < '17:00' ? 'afternoon' : 'evening');

/** "Dr. Ravi Kumar" → "RK" (same rule as the public pages' monograms). */
export const initials = (name: string) =>
  name.replace(/^dr\.?\s+/i, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

/** Only an http(s) URL to a public-site photo (…/media/<hash>.jpg) is used; anything else shows initials. */
export function safePhotoUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  try {
    const u = new URL(url);
    return /^https?:$/.test(u.protocol) && /^\/media\/[a-f0-9]+\.jpg$/.test(u.pathname) && !u.search && !u.username
      ? u.href : undefined;
  } catch {
    return undefined;
  }
}

/** Whole years between a yyyy-mm-dd date of birth and today; null when not a valid date. */
export function ageFrom(dob: string): number | null {
  if (!DATE.test(dob)) return null;
  const [y, m, d] = dob.split('-').map(Number);
  const now = new Date();
  let age = now.getFullYear() - y;
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age -= 1;
  return age;
}

/** A thrown error → text for the patient (rate limits get their own wording). */
export function errorText(e: unknown, fallback: string, tooMany: (s: number) => string): string {
  if (e instanceof RateLimitError) return tooMany(e.retryAfterSeconds);
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

/** An API result's error carried as an Error, keeping the server's code for branching. */
export class BookingApiError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'BookingApiError';
  }
}

export function useCareTheme() {
  const outer = useTheme();
  return useMemo(() => createTheme(outer, {
    palette: {
      primary: { main: CARE.action, dark: CARE.actionDeep, contrastText: '#fff' },
      success: { main: CARE.verified },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
      MuiToggleButton: { styleOverrides: { root: { textTransform: 'none' } } },
    },
  }), [outer]);
}
